import type { KeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { CachedImage } from '~/components/common/cached-image/cached-image';
import { Skeleton } from '~/components/ui/skeleton';
import { cn } from '~/utils/cn';

import type { Product } from '../../types/catalog-operations.types';
import { RecuratedSinceChip } from '../chips/recurated-since-chip';

interface Props {
  products: Product[];
  selectedId: string | null;
  onSelect: (product: Product) => void;
  isLoading: boolean;
  onKeyDown: (e: KeyboardEvent<HTMLUListElement>) => void;
  /** E2E `data-testid` root; children derive `${testId}-...`. */
  testId?: string;
}

/**
 * Master list for the pre-delivery preview step — one row per curated product
 * in this batch. Deliberately simpler than `DeliveryCompareProductRail`: there
 * is no delivery outcome to badge yet, since nothing has been delivered.
 * `role="listbox"`/`role="option"` — the container's arrow-key handling lives
 * in the parent step, which owns pagination and selection.
 *
 * The product name clamps to 2 lines (a 1-line truncation made same-named
 * variants indistinguishable), and the retailer id shows only its last 6
 * digits — every row's id shares the same long prefix, which wasted the
 * row's width for no information.
 */
export function DeliveryWizardPreviewRail({
  products,
  selectedId,
  onSelect,
  isLoading,
  onKeyDown,
  testId = 'delivery-wizard-preview-rail',
}: Props) {
  const { t } = useTranslation('catalog-operations');
  const { t: tCommon } = useTranslation('common');

  if (isLoading) {
    return (
      <div className="space-y-2 p-2" data-testid={`${testId}-loading`}>
        {Array.from({ length: 6 }, (_, i) => (
          <Skeleton key={i} className="h-16 w-full rounded-lg" />
        ))}
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div
        className="text-muted-foreground flex h-32 items-center justify-center px-4 text-center text-sm"
        data-testid={`${testId}-empty`}
      >
        {t('deliveryCompare.rail.empty')}
      </div>
    );
  }

  return (
    <ul
      role="listbox"
      aria-label={tCommon('deliveryWizard.preview.rail.ariaLabel')}
      tabIndex={0}
      onKeyDown={onKeyDown}
      data-testid={`${testId}-list`}
      className="focus-visible:ring-ring space-y-1.5 rounded-lg p-2 focus-visible:ring-2 focus-visible:outline-none"
    >
      {products.map((product) => {
        const isActive = product.id === selectedId;
        const shortId = product.sourceProductId.slice(-6);
        return (
          <li key={product.id} role="presentation">
            <button
              type="button"
              role="option"
              aria-selected={isActive}
              data-product-id={product.id}
              data-testid={`${testId}-item-${product.id}`}
              onClick={() => {
                onSelect(product);
              }}
              className={cn(
                'w-full cursor-pointer rounded-lg border px-3 py-2.5 text-left transition-all',
                isActive
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border hover:border-primary/40 hover:bg-muted/40 bg-white',
              )}
            >
              <div className="flex items-start gap-3">
                <CachedImage
                  src={product.imageUrl}
                  alt={product.title}
                  className="h-11 w-11 shrink-0 rounded-md border"
                />
                <div className="min-w-0 flex-1 space-y-1">
                  <p
                    className={cn(
                      'line-clamp-2 text-sm leading-snug font-semibold',
                      isActive ? 'text-primary' : 'text-foreground',
                    )}
                  >
                    {product.title}
                  </p>
                  <p className="text-muted-foreground truncate text-xs tabular-nums">
                    {tCommon('deliveryWizard.preview.rail.shortId', { id: shortId })}
                  </p>
                  <RecuratedSinceChip product={product} className="text-[10px]" />
                </div>
              </div>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
